import { ReactNode, useState } from 'react';

import { IconAngleLeft, IconQuestionMarkFilled } from '@iwoca/icons';
import { IwocaLogo } from '@iwoca/orion';
import { useLingui } from '@lingui/react/macro';

import styles from './MobileSignupLayout.module.css';
import { Button } from '../../Button/Button';
import { MobileSignupHelpModal } from '../MobileSignupHelpModal/MobileSignupHelpModal';

type TMobileSignupLayoutProps = {
  /** 1-based position + total, drives the progress bar and "Step X of N". */
  progress: { current: number; total: number };
  children: ReactNode;
  onBack?: () => void;
};

/**
 * The constant outer frame for the whole mobile signup flow (back + logo + help
 * + progress). Rendered once by the container and reconciled in place across
 * step changes, so this chrome never remounts — only `children` animates.
 */
export const MobileSignupLayout = ({
  progress,
  children,
  onBack,
}: TMobileSignupLayoutProps) => {
  const { t } = useLingui();
  const [helpOpen, setHelpOpen] = useState(false);
  const { current, total } = progress;
  const filledPercentage = total > 0 ? (current / total) * 100 : 0;

  return (
    <div className={styles.layout}>
      <header className={styles.header}>
        <div className={styles.slot}>
          {onBack && (
            <Button
              variant="tertiary"
              size="small"
              iconOnly
              onClick={onBack}
              aria-label={t`Back`}
            >
              <IconAngleLeft />
            </Button>
          )}
        </div>

        <IwocaLogo color="red" className={styles.logo} />

        <div className={styles.slotEnd}>
          <Button
            variant="tertiary"
            size="small"
            iconOnly
            onClick={() => setHelpOpen(true)}
            aria-label={t`Help`}
          >
            <IconQuestionMarkFilled />
          </Button>
        </div>
      </header>

      <div className={styles.progress}>
        <div className={styles.track}>
          <div
            className={styles.bar}
            style={{ width: `${filledPercentage}%` }}
            role="progressbar"
            aria-valuenow={current}
            aria-valuemin={1}
            aria-valuemax={total}
          />
        </div>
        <span
          className={styles.stepCount}
        >{t`Step ${current} of ${total}`}</span>
      </div>

      {children}

      <MobileSignupHelpModal
        isOpen={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
    </div>
  );
};
